import { FeedbackError } from '@/components/Feedback';
import { CustomTableSkeleton } from '@/components_v2/Table/TableSkeleton/CustomTableSkeleton';
import fetchCardData from '@services/query/fetchCardData';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import Table from '../../../..';
import { TableRequestProps } from '../../../../interfaces';
import { Center, EmptyData, SpaceBar } from './styles';

const TableRequest: React.FC<TableRequestProps> = ({
  data,
  cancelToken,
  parentExpanded,
  level,
  expandHeight,
  filters = undefined,
  tableView,
}) => {
  const { url, minHeight, maxHeight, lineColor } = data;
  const [childHeight, setChildHeight] = useState<number>(0);

  const fetchPage = useCallback(
    async ({ pageParam = 1 }) => {
      if (!url) {
        FeedbackError({ mainText: 'URL não informada' });
        return { items: [], page: pageParam, meta: null };
      }

      const result = await fetchCardData(
        url,
        {
          filters: filters || undefined,
          page: `${pageParam}`,
        },
        cancelToken,
      );

      if (!result || !result.content) {
        return { items: [], page: pageParam, meta: null };
      }

      const { items: _items, ...contentWithoutItems } = result.content;

      return {
        items: result.content.items || [],
        page: pageParam + 1,
        meta: pageParam === 1 ? contentWithoutItems : null,
      };
    },
    [url, filters, cancelToken],
  );

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    ['tableRequest', url, filters],
    fetchPage,
    {
      getNextPageParam: (lastPage) =>
        lastPage?.items?.length > 0 ? lastPage.page : false,
      refetchOnWindowFocus: false,
      enabled: !!url,
    },
  );

  const contentMeta = useMemo(
    () => queryData?.pages?.[0]?.meta || null,
    [queryData?.pages],
  );

  const allItems = useMemo(
    () => queryData?.pages?.flatMap((p) => p.items) || [],
    [queryData?.pages],
  );

  const content = useMemo(
    () => (contentMeta ? { ...contentMeta, items: allItems } : ({} as any)),
    [contentMeta, allItems],
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const { scrollHeight, scrollTop, clientHeight } = target;
      if (
        scrollHeight - scrollTop - clientHeight < 300 &&
        !isFetchingNextPage &&
        hasNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage],
  );

  const rowHeight = content?.rowHeight || 35;
  const headerHeight = rowHeight;
  const footerHeight = content?.footer && content.footer.length > 0 ? rowHeight : 0;
  const itemsLength = allItems?.length || 0;

  const totalVisualHeight =
    itemsLength * rowHeight + headerHeight + footerHeight;

  const effectiveVisualHeight = maxHeight
    ? Math.min(
        totalVisualHeight,
        typeof maxHeight === 'string' ? parseInt(maxHeight, 10) : maxHeight,
      )
    : totalVisualHeight;

  useEffect(() => {
    expandHeight && expandHeight(effectiveVisualHeight + 16);

    return () => {
      expandHeight && expandHeight(0);
    };
  }, [effectiveVisualHeight, expandHeight]);

  const renderContent = useMemo(() => {
    if (isLoading) {
      return (
        <Center>
          <CustomTableSkeleton count={30} />
        </Center>
      );
    }

    if (!url) {
      return (
        <Center>
          <EmptyData>Erro ao tentar pegar dados!</EmptyData>
        </Center>
      );
    }

    if (!content || !content.items || content.items.length <= 0) {
      return (
        <Center>
          <EmptyData>Sem dados</EmptyData>
        </Center>
      );
    }

    const tablePropHeight = effectiveVisualHeight + headerHeight;

    return (
      <div
        style={{
          height: effectiveVisualHeight + childHeight + 16,
          ...(maxHeight ? { maxHeight, ...(content?.isPaginate ? { overflow: 'auto' } : {}) } : {}),
          ...(minHeight ? { minHeight } : {}),
          width: '100%',
        }}
        onScroll={handleScroll}
      >
        <div
          style={{
            height: effectiveVisualHeight + childHeight + 16,
            minHeight,
            ...(maxHeight
              ? {
                maxHeight:
                  typeof maxHeight === 'string' ? maxHeight : maxHeight - 5,
              }
              : {}),
            width: '100%',
          }}
        >
          <Table
            columns={content.columns}
            items={content.items}
            headerHeight={headerHeight}
            height="100%"
            searchKey=""
            parentExpanded={parentExpanded}
            level={level}
            setChildHeight={setChildHeight}
            tableView={tableView}
            {...content}
          />
          {isFetchingNextPage && <CustomTableSkeleton count={10} />}
          <SpaceBar color={lineColor || '#8c8c8c'} />
        </div>
      </div>
    );
  }, [
    isLoading,
    url,
    content,
    content.items,
    effectiveVisualHeight,
    headerHeight,
    childHeight,
    maxHeight,
    parentExpanded,
    level,
    lineColor,
    tableView,
    handleScroll,
    isFetchingNextPage,
  ]);

  return renderContent;
};

export default TableRequest;
